import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/utils-extra";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to={user.must_change_password ? "/change-password" : (user.role === "super_admin" ? "/admin/dashboard" : "/dashboard")} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u?.must_change_password) {
        navigate("/change-password");
        return;
      }
      toast.success("Welcome back!");
      navigate(u?.role === "super_admin" ? "/admin/dashboard" : "/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]" data-testid="login-page">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxzbGVlcCUyMGNsaW5pYyUyMHBhdGllbnQlMjBkb2N0b3J8ZW58MHx8fHwxNzgwMTI5NzUzfDA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/80 via-[#0F172A]/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#0F172A] flex items-center justify-center overflow-hidden ring-1 ring-white/10">
              <img src="/remediscore-logo.png" alt="RemediScore" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <div className="font-display font-semibold text-2xl">RemediScore</div>
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">Performance Suite</div>
            </div>
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-tight max-w-md">
            Measure what matters.<br />
            <span className="opacity-80">Coach what counts.</span>
          </h1>
          <p className="mt-4 text-white/80 max-w-md text-base">
            Fair, measurable performance reviews — KPIs, bonuses, and coaching in one place.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md surface-card border-[#E2E8F0]">
          <CardContent className="p-8 sm:p-10">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center overflow-hidden">
                <img src="/remediscore-logo.png" alt="RemediScore" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <div className="font-display font-semibold text-lg">RemediScore</div>
                <div className="small-label">Performance Suite</div>
              </div>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-1">Sign in</h2>
            <p className="text-sm text-[#475569] mb-8">Welcome back. Let's get to work.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="small-label">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="mt-2 h-11"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <Label htmlFor="password" className="small-label">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    data-testid="toggle-password-button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569]"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {err && (
                <div className="text-sm text-[#DC2626] bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                  {err}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={busy}
                className="w-full h-11 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-xl"
              >
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
